import { z } from 'zod';

export const fiscalStatusSchema = z.enum([
  'AWAITING_REPORT',
  'IN_REVIEW',
  'MATCHED',
  'DIVERGENT',
  'NFSE_EMITTED',
  'NFSE_SENT',
  'PAID',
  'CANCELED',
]);
export type FiscalStatus = z.infer<typeof fiscalStatusSchema>;

export const fiscalRecordSchema = z.object({
  id: z.string().uuid(),
  userId: z.string().uuid(),
  cnpjId: z.string().uuid(),
  serviceId: z.string().uuid(),
  referenceMonth: z.string(),
  computedProduction: z.number(),
  reportedValue: z.number().nullable(),
  tolerancePctAtTime: z.number(),
  diffAbs: z.number().nullable(),
  diffPct: z.number().nullable(),
  status: fiscalStatusSchema,
  notes: z.string().nullable(),
  paidAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type FiscalRecord = z.infer<typeof fiscalRecordSchema>;

export const fiscalRecordServiceSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  color: z.string(),
});

export const fiscalRecordViewSchema = z.object({
  record: fiscalRecordSchema,
  service: fiscalRecordServiceSchema.nullable(),
});
export type FiscalRecordView = z.infer<typeof fiscalRecordViewSchema>;

export const ocrResultSchema = z.object({
  reportedValue: z.number().nullable().optional(),
  currency: z.string().optional(),
  referenceMonth: z.string().nullable().optional(),
  serviceName: z.string().nullable().optional(),
  items: z
    .array(
      z.object({
        description: z.string(),
        quantity: z.number(),
        unitValue: z.number(),
      }),
    )
    .optional(),
  confidence: z.number().optional(),
  rawText: z.string().optional(),
});
export type OcrResult = z.infer<typeof ocrResultSchema>;

export const fiscalReportSchema = z.object({
  id: z.string().uuid(),
  fiscalRecordId: z.string().uuid(),
  gcsObjectPath: z.string(),
  mimeType: z.string(),
  sizeBytes: z.number(),
  source: z.enum(['EMAIL_INBOUND', 'MANUAL_UPLOAD']),
  sourceMessageId: z.string().nullable(),
  ocrStatus: z.enum(['PENDING', 'PROCESSING', 'DONE', 'FAILED']),
  ocrResult: ocrResultSchema.nullable(),
  ocrError: z.string().nullable(),
  ocrProvider: z.string(),
  ocrConfidence: z.number().nullable(),
  createdAt: z.string(),
});
export type FiscalReport = z.infer<typeof fiscalReportSchema>;

export const nfseIssuanceSchema = z.object({
  id: z.string().uuid(),
  fiscalRecordId: z.string().uuid(),
  provider: z.string(),
  providerTxId: z.string().nullable(),
  nfseNumber: z.string().nullable(),
  verificationCode: z.string().nullable(),
  xmlGcsPath: z.string().nullable(),
  pdfGcsPath: z.string().nullable(),
  status: z.enum(['QUEUED', 'SENT', 'ACCEPTED', 'REJECTED', 'CANCELED', 'FAILED']),
  errorPayload: z.unknown().nullable(),
  retryCount: z.number(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type NfseIssuance = z.infer<typeof nfseIssuanceSchema>;

export const fiscalRecordDetailSchema = z.object({
  record: fiscalRecordSchema,
  service: fiscalRecordServiceSchema.nullable(),
  reports: z.array(fiscalReportSchema),
  nfseIssuances: z.array(nfseIssuanceSchema),
});
export type FiscalRecordDetail = z.infer<typeof fiscalRecordDetailSchema>;

export const releaseDivergenceSchema = z.object({
  reason: z
    .string()
    .min(10, 'Justificativa mínima de 10 caracteres')
    .max(2000, 'Máximo 2000 caracteres'),
});
export type ReleaseDivergenceInput = z.infer<typeof releaseDivergenceSchema>;

export const markPaidSchema = z.object({
  paidAt: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Data inválida'),
});
export type MarkPaidInput = z.infer<typeof markPaidSchema>;

export const FISCAL_STATUS_LABEL: Record<FiscalStatus, string> = {
  AWAITING_REPORT: 'Aguardando Relatório',
  IN_REVIEW: 'Em Conferência',
  MATCHED: 'Valores Conferem',
  DIVERGENT: 'Divergente',
  NFSE_EMITTED: 'NF Emitida',
  NFSE_SENT: 'NF Enviada',
  PAID: 'Pago',
  CANCELED: 'Cancelado',
};
